import { Input } from "@components/ui/input";
import { PlusCircleIcon, Trash2, Trash2Icon } from "lucide-react";
import dayjs from "@lib/dayjs";
import {
  CreateEventFormValues,
  durationType,
} from "pages/CalendarsPage/SchedulingSection/form.types";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

const TimeSlotSelector = ({ date, updatedSlots, isMeetingInvite = true }) => {
  const methods = useFormContext<CreateEventFormValues>();
  const [flipped, setFlipped] = useState(false);

  const { fields, append, remove, replace } = useFieldArray({
    control: methods?.control,
    name: `availabilitySlots.${date}`,
  });

  const handleAddSlot = () => {
    const meetingDuration = methods?.getValues("meetingDuration.duration");
    const meetingDurationType = methods?.getValues("meetingDuration.type");
    const now = dayjs();
    const startTime = dayjs(date)
      .set("hour", now.hour())
      .set("minute", now.minute())
      .set("second", now.second())
      .set("millisecond", now.millisecond())
      .toISOString();

    append({
      startTime,
      endTime: dayjs(startTime)
        .add(
          meetingDuration,
          meetingDurationType === durationType.hour ? "hours" : "minutes"
        )
        .toISOString(),
    });
  };

  const handleDeleteDate = () => {
    const availabilitySlots = methods.getValues("availabilitySlots");
    const updatedAvailabiltySlots = { ...availabilitySlots };
    delete updatedAvailabiltySlots[date];
    methods?.setValue("availabilitySlots", updatedAvailabiltySlots, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (updatedSlots) {
      replace(updatedSlots);
    }
  }, [updatedSlots]);

  if (!fields.length) return;

  return (
    <div className="flex gap-8 border-b py-3">
      <div
        className="w-14 h-10 [perspective:100px]"
        onMouseEnter={() => setFlipped(true)}
        onMouseLeave={() => setFlipped(false)}
      >
        <div
          className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] ${
            flipped ? "[transform:rotateY(180deg)]" : ""
          }`}
        >
          {/* Front */}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary text-white rounded-md py-1 px-2 cursor-pointer [backface-visibility:hidden]">
            <span className="font-bold text-sm">
              {dayjs(date).format("ddd")}
            </span>
            <span className="font-semibold text-[0.6rem]">
              {dayjs(date).format("MMM-DD")}
            </span>
          </div>

          {/* Back */}
          <button
            onClick={handleDeleteDate}
            className="absolute inset-0 flex items-center justify-center bg-red-600 text-white rounded-md [transform:rotateY(180deg)] [backface-visibility:hidden]"
          >
            <Trash2Icon className="w-4 h-4" color="white" />
          </button>
        </div>
      </div>

      {/* Timeslots list */}
      <div className="flex flex-col gap-3">
        {fields?.map((field, index) => {
          return (
            <div className="flex gap-8">
              <div className="flex gap-3 h-10 rounded-md items-center">
                <Input
                  className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none h-full w-max"
                  type="time"
                  value={dayjs(field?.startTime).format("HH:mm")}
                  // step={'15'}
                />
                <div className="h-px w-5 bg-gray-300" />
                <Input
                  className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none h-full w-max"
                  type="time"
                  value={dayjs(field?.endTime).format("HH:mm")}
                  // step={'15'}
                />
              </div>
              {/* Action buttons */}
              <div className="flex gap-1 bg-gray-50 rounded-md">
                <button
                  onClick={() => remove(index)}
                  className="flex items-center justify-center px-3 h-10 hover:bg-rose-200 rounded-md"
                >
                  <Trash2 className="h-4 w-4" color="gray" />
                </button>
                {index === fields?.length - 1 && !isMeetingInvite ? (
                  <button
                    onClick={handleAddSlot}
                    className="flex items-center justify-center px-3 h-10 hover:bg-green-200 rounded-md"
                  >
                    <PlusCircleIcon className="h-4 w-4" color="gray" />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimeSlotSelector;
